import { jsxs, jsx } from "react/jsx-runtime";
import { useRef, useState, useLayoutEffect, useEffect, useCallback } from "react";
import styled from "styled-components";
import { typographyCss, colors, cbSemanticCssVars } from "@creditornot/cb-ingredients";
import { createCssVars, createCssVarDefinitions, pxToRem, createThemedCssVarDefinitions } from "@creditornot/cb-utils";
const sizeCssVars = createCssVars(["height", "fontSize", "borderRadius"]);
const colorCssVars = createCssVars([
  "background",
  "segmentBackground",
  "segmentDivider",
  "segmentLabelPressed",
  "segmentBoxShadow"
]);
const Root = styled.div`
  ${({ $cssVarOverrides }) => createCssVarDefinitions({
  borderRadius: "8px",
  fontSize: pxToRem(14),
  height: "36px",
  ...$cssVarOverrides
})}

  ${typographyCss.baseFontFamily()}

  ${createThemedCssVarDefinitions({
  background: {
    light: colors.pepper8,
    dark: colors.salt12
  },
  segmentBackground: {
    light: colors.salt100,
    dark: colors.salt24
  },
  segmentDivider: {
    light: colors.pepper8,
    dark: colors.salt12
  },
  segmentLabelPressed: {
    light: colors.pepper80,
    dark: colors.salt88
  },
  segmentBoxShadow: {
    light: "0 1px 1px 0 rgba(0, 0, 0, 0.12), 0 0 1px 0 rgba(0, 0, 0, 0.06)",
    dark: "none"
  }
})}

  box-sizing: border-box;
  user-select: none;
  z-index: 0;

  position: relative;
  display: inline-flex;
  align-items: center;
  height: ${sizeCssVars.height};
  padding: 2px;
  border-radius: ${sizeCssVars.borderRadius};
  background-color: ${colorCssVars.background};
  font-size: ${sizeCssVars.fontSize};
  font-weight: 400;
  cursor: pointer;

  &[data-fixed-widths='true'] {
    display: flex;
  }

  &[aria-disabled] {
    cursor: not-allowed;
  }
`;
const FocusIndicator = styled.div`
  visibility: hidden;
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  border-radius: ${sizeCssVars.borderRadius};
  border: 2px solid ${cbSemanticCssVars.a11yFocusIndicator};
  z-index: 4;
`;
const ItemLabel = styled.span`
  z-index: 3;
`;
const ItemOverlayLabel = styled.span`
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  right: 0;

  display: flex;
  align-items: center;
  justify-content: center;

  font-weight: 700;
  color: inherit;
  transition: opacity 70ms;
  opacity: 0;
  z-index: 4;

  &[data-show='true'] {
    opacity: 1;
  }
`;
const Segment = styled.div`
  /* normalize */
  font-family: inherit;
  font-size: inherit;
  line-height: inherit;
  border: none;
  position: relative;
  overflow: visible;
  text-transform: none;

  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  box-sizing: border-box;
  user-select: none;

  display: flex;
  justify-content: center;
  align-items: center;
  height: calc(${sizeCssVars.height} - 4px);
  flex: none;
  padding: 0 24px;
  text-align: center;
  outline: none;
  color: ${cbSemanticCssVars.textSecondary};
  white-space: nowrap;

  &[data-fixed-widths='true'] {
    flex: 1;
    padding: 0;
  }

  &[data-active='true'] ${ItemLabel} {
    color: transparent;
    transition: color 70ms;
  }

  &[aria-disabled] {
    cursor: not-allowed;
    color: ${cbSemanticCssVars.textTertiary};
  }

  &:not([aria-disabled]):hover {
    color: ${cbSemanticCssVars.textPrimary};
  }

  &:not([aria-disabled]):active,
  &:not([aria-disabled]):active:hover {
    color: ${colorCssVars.segmentLabelPressed};
  }

  &:not([aria-disabled])[data-active='true'] {
    color: ${cbSemanticCssVars.textPrimary};
  }

  &::after {
    content: '';
    position: absolute;
    left: 0;
    height: calc(${sizeCssVars.height} - 16px);
    width: 1px;
    opacity: 0;
    background: ${colorCssVars.segmentDivider};
    transition: opacity 150ms;
    transition-delay: 50ms;
  }

  &[data-show-divider='true']::after {
    opacity: 1;
  }

  :focus-visible ${FocusIndicator} {
    visibility: visible;
  }
`;
const SegmentBackground = styled.div`
  box-sizing: border-box;
  position: absolute;
  height: calc(${sizeCssVars.height} - 4px);
  z-index: 2;

  background-color: ${colorCssVars.segmentBackground};
  box-shadow: ${colorCssVars.segmentBoxShadow};
  border-radius: ${sizeCssVars.borderRadius};

  left: ${({ $left }) => `${$left}px`};
  width: ${({ $width }) => `${$width}px`};
  transition: ${({ $duration }) => `left ${$duration} ease-in-out, width ${$duration} ease-in-out`};
`;
const SegmentedControl = ({
  className,
  cssVarOverrides,
  disabled,
  fixedWidths,
  onChange,
  options,
  style,
  value
}) => {
  const activeEl = useRef(null);
  const [isFirstRender, setIsFirstRender] = useState(true);
  const [bgState, setBgState] = useState({
    left: 0,
    width: 0,
    duration: "0ms"
  });
  useLayoutEffect(() => {
    if (activeEl.current) {
      const { offsetLeft: left, offsetWidth: width } = activeEl.current;
      setBgState({ left, width, duration: isFirstRender ? "0ms" : "200ms" });
      if (isFirstRender) {
        setIsFirstRender(false);
      }
    }
  }, [value, fixedWidths]);
  useEffect(() => {
    if (!fixedWidths || !window)
      return;
    const handleResize = () => {
      if (activeEl.current) {
        const { offsetLeft: left, offsetWidth: width } = activeEl.current;
        setBgState({ left, width, duration: "0ms" });
      }
    };
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [fixedWidths]);
  const handleChange = useCallback(
    (newValue) => {
      if (!disabled && newValue !== value && onChange) {
        onChange(newValue);
      }
    },
    [value, disabled, onChange]
  );
  return /* @__PURE__ */ jsxs(
    Root,
    {
      $cssVarOverrides: cssVarOverrides,
      "aria-disabled": disabled || void 0,
      className,
      "data-fixed-widths": fixedWidths,
      role: "group",
      style,
      children: [
        options.map(({ disabled: disabledOption, label, value: optionValue }, index) => {
          var _a;
          return /* @__PURE__ */ jsxs(
            Segment,
            {
              "aria-disabled": disabled || disabledOption ? true : void 0,
              "aria-pressed": value === optionValue,
              "data-active": value === optionValue,
              "data-fixed-widths": fixedWidths,
              "data-show-divider": value !== optionValue && ((_a = options[index - 1]) == null ? void 0 : _a.value) !== value && index > 0,
              onClick: () => {
                if (!disabledOption)
                  handleChange(optionValue);
              },
              onKeyDown: (event) => {
                if (["ArrowRight", "ArrowDown", "ArrowLeft", "ArrowUp"].includes(event.key)) {
                  event.preventDefault();
                  const enabledOptions = options.filter((option) => !option.disabled);
                  const index2 = enabledOptions.findIndex((option) => option.value === optionValue);
                  const newIndex = event.key === "ArrowRight" || event.key === "ArrowDown" ? index2 + 1 : index2 - 1;
                  if (newIndex >= 0 && newIndex < enabledOptions.length) {
                    handleChange(enabledOptions[newIndex].value);
                    setTimeout(() => {
                      var _a2;
                      (_a2 = activeEl.current) == null ? void 0 : _a2.focus();
                    });
                  }
                }
                if (event.key === " ") {
                  event.preventDefault();
                  handleChange(optionValue);
                  setTimeout(() => {
                    var _a2;
                    (_a2 = activeEl.current) == null ? void 0 : _a2.focus();
                  });
                }
              },
              ref: value === optionValue ? activeEl : null,
              role: "button",
              tabIndex: disabledOption ? -1 : 0,
              children: [
                /* @__PURE__ */ jsx(ItemLabel, { children: label }),
                /* @__PURE__ */ jsx(ItemOverlayLabel, { "aria-hidden": true, "data-show": value === optionValue, children: label }),
                /* @__PURE__ */ jsx(FocusIndicator, {})
              ]
            },
            label
          );
        }),
        /* @__PURE__ */ jsx(SegmentBackground, { $duration: bgState.duration, $left: bgState.left, $width: bgState.width })
      ]
    }
  );
};
export {
  SegmentedControl
};
//# sourceMappingURL=index.js.map
